import * as THREE from 'three';
import { clamp } from '@/core/math/scalar';

/**
 * Screen-space reflection capture for the ocean.
 *
 * The ocean fragment shader reflects its view ray and ray-marches it against
 * a depth buffer to recover what the water should reflect. That buffer has to
 * be *this frame's* opaque world with the ocean itself absent — if it weren't
 * absent the reflected ray would re-hit the water's own surface and regress
 * forever. This class owns that capture:
 *
 *   - A render target holding the opaque world's colour plus its depth.
 *   - A single capture camera spanning the whole near→mid range. It shares the
 *     shared view orientation/fov/aspect (it is driven through
 *     `LayeredRenderer.cameras`), so a screen-space march against its depth
 *     maps one-to-one onto the main view.
 *   - The depth is the hardware depth buffer (a `DepthTexture`), reconstructed
 *     to linear view-space distance in the shader. One camera over 0.25 m to
 *     ~100 km loses precision with range, but the reflections that matter most
 *     are the near ones (coastline, cliffs, the vessel), and far misses fall
 *     back to the procedural sky reflection without anyone noticing.
 *
 * The entire pass is best-effort: if it is off (low/medium quality) the ocean
 * simply keeps its existing procedural sky reflection.
 */

/** Nearest point the capture must resolve: centimetre-scale is never reflected. */
export const SSR_NEAR_M = 0.25;
/** Base far plane; grows with altitude so distant cliffs stay capturable. */
const SSR_FAR_BASE_M = 120_000;
/** Far-plane growth per metre of altitude. */
const SSR_FAR_ALTITUDE_FACTOR = 4;
/** Hard cap on the far plane — past this terrain isn't worth reflecting. */
const SSR_FAR_MAX_M = 300_000;

export class ScreenSpaceReflection {
  readonly camera: THREE.PerspectiveCamera;
  target: THREE.WebGLRenderTarget;
  private targetW = 0;
  private targetH = 0;
  private readonly scale: number;

  constructor(renderer: THREE.WebGLRenderer, scale: number, fovDegrees: number) {
    this.scale = scale;

    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    this.camera = new THREE.PerspectiveCamera(fovDegrees, size.x / Math.max(1, size.y), SSR_NEAR_M, SSR_FAR_BASE_M);
    // Only the plain world layer: this excludes the sky/atmosphere shells
    // (RENDER_LAYER.sky) and the clouds (RENDER_LAYER.clouds) so the capture
    // holds solid terrain/the vessel, not the things the ocean reflects
    // procedurally. The ocean itself is excluded by visibility, see `capture`.
    this.camera.layers = new THREE.Layers();
    this.camera.layers.enable(0);
    this.camera.updateProjectionMatrix();

    this.target = this.makeTarget(size.x * scale, size.y * scale);
    this.targetW = this.target.width;
    this.targetH = this.target.height;
  }

  private makeTarget(w: number, h: number): THREE.WebGLRenderTarget {
    const width = Math.max(1, Math.floor(w));
    const height = Math.max(1, Math.floor(h));
    const depthTexture = new THREE.DepthTexture(width, height);
    depthTexture.minFilter = THREE.NearestFilter;
    depthTexture.magFilter = THREE.NearestFilter;
    depthTexture.format = THREE.DepthFormat;
    depthTexture.type = THREE.UnsignedIntType;
    const target = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: true,
      depthTexture,
      stencilBuffer: false,
    });
    return target;
  }

  /** Reallocates the capture when the render size changes. Idempotent. */
  resize(renderer: THREE.WebGLRenderer): void {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const w = Math.max(1, Math.floor(size.x * this.scale));
    const h = Math.max(1, Math.floor(size.y * this.scale));
    if (w !== this.targetW || h !== this.targetH) {
      this.target.dispose();
      this.targetW = w;
      this.targetH = h;
      this.target = this.makeTarget(w, h);
    }
  }

  /**
   * Renders the opaque world into the capture with the ocean hidden, so the
   * ocean shader can reflect this frame's scene. Call once per frame, after the
   * shadow cascades and before the world passes that draw the ocean.
   */
  capture(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    ssrExcluded: ReadonlySet<THREE.Object3D>,
    altitude: number,
  ): void {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    this.camera.aspect = size.x / Math.max(1, size.y);
    const far = clamp(SSR_FAR_BASE_M + altitude * SSR_FAR_ALTITUDE_FACTOR, SSR_FAR_BASE_M, SSR_FAR_MAX_M);
    if (this.camera.far !== far) {
      this.camera.far = far;
      this.camera.updateProjectionMatrix();
    }

    // Hide the ocean (and anything else registered) for this pass only,
    // restoring each object's prior visibility afterwards.
    const prior: boolean[] = [];
    let i = 0;
    for (const o of ssrExcluded) {
      prior.push(o.visible);
      o.visible = false;
      i++;
    }

    const previousTarget = renderer.getRenderTarget();
    const previousClearAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(this.target);
    // Alpha is reserved for TerrainMaterial's explicit dry-land mask. Empty
    // capture pixels must therefore mean "no land", not the renderer's normal
    // opaque-canvas clear alpha of one.
    renderer.setClearAlpha(0);
    renderer.clear(true, true, true);
    renderer.render(scene, this.camera);
    renderer.setClearAlpha(previousClearAlpha);
    renderer.setRenderTarget(previousTarget);

    i = 0;
    for (const o of ssrExcluded) {
      o.visible = prior[i];
      i++;
    }
  }

  dispose(): void {
    this.target.dispose();
    this.target.depthTexture?.dispose();
  }
}
